import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { CheckCircle2, Award, Users, Clock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
export function AboutPage() {
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-202">
      <Navbar data-id="element-203" />

      <main className="flex-grow pt-24" data-id="element-204">
        {/* Hero / Intro Section */}
        <Section className="relative overflow-hidden" data-id="element-205">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-206">
            {/* Image Column */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} className="relative max-w-md mx-auto lg:mx-0" data-id="element-207">
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border-8 border-white bg-gray-200" data-id="element-208">
                {/* Profile Picture Placeholder */}
                <img src="https://cdn.magicpatterns.com/uploads/exw4rZtFTQrttLJZveReLJ/IMG_3419.jpg" alt="Construction Professional" className="w-full h-full object-cover" data-id="element-209" />

                {/* Experience Badge */}
                <div className="absolute bottom-6 right-6 bg-white/95 backdrop-blur p-4 rounded-xl shadow-lg border border-gray-100 max-w-[200px]" data-id="element-210">
                  <div className="flex items-center gap-3 mb-1" data-id="element-211">
                    <div className="p-2 bg-[#17a2b8]/10 rounded-full text-[#17a2b8]" data-id="element-212">
                      <Award className="h-6 w-6" data-id="element-213" />
                    </div>
                    <div data-id="element-214">
                      <span className="block text-2xl font-bold text-[#2c2c2c]" data-id="element-215">
                        10+
                      </span>
                      <span className="text-xs text-[#525252] font-medium uppercase tracking-wider" data-id="element-216">
                        Years Exp.
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative Background Element */}
              <div className="absolute -z-10 top-10 -left-10 w-full h-full bg-[#17a2b8]/5 rounded-2xl transform -rotate-3" data-id="element-217"></div>
            </motion.div>

            {/* Content Column */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-218">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#17a2b8]/10 text-[#17a2b8] text-sm font-semibold mb-6" data-id="element-219">
                About The Owner
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-[#2c2c2c] mb-6" data-id="element-220">
                Building Trust, <br data-id="element-221" />
                <span className="text-[#17a2b8]" data-id="element-222">Delivering Excellence.</span>
              </h1>

              <div className="space-y-6 text-lg text-[#525252] leading-relaxed" data-id="element-223">
                <p data-id="element-224">
                  I'm a seasoned construction professional with over a decade of
                  experience delivering high-quality projects with a focus on
                  client satisfaction.
                </p>
                <p data-id="element-225">
                  As the owner of a construction company, I'm dedicated to
                  building strong relationships, ensuring seamless
                  communication, and exceeding expectations through superior
                  workmanship. My commitment to excellence and attention to
                  detail have earned me a reputation for reliability,
                  efficiency, and craftsmanship that is respected throughout the
                  Portland area.
                </p>
                <p data-id="element-226">
                  Whether managing large-scale builds or intricate renovations,
                  I prioritize integrity, quality, and a client-first approach
                  in every project.
                </p>
              </div>

              <div className="mt-10 grid sm:grid-cols-2 gap-4" data-id="element-227">
                {['Client-First Approach', 'Superior Workmanship', 'Seamless Communication', 'Reliable & Efficient'].map((item, i) => <div key={i} className="flex items-center gap-2 text-[#2c2c2c] font-medium" data-id="element-228">
                    <CheckCircle2 className="h-5 w-5 text-[#17a2b8]" data-id="element-229" />
                    {item}
                  </div>)}
              </div>

              <div className="mt-10" data-id="element-230">
                <Button variant="primary" size="lg" rightIcon={<ArrowRight className="h-5 w-5" data-id="element-232" />} onClick={() => window.location.href = '/#contact'} data-id="element-231">
                  Work With Me
                </Button>
              </div>
            </motion.div>
          </div>
        </Section>

        {/* Values Section */}
        <Section background="white" data-id="element-233">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-234">
            <h2 className="text-3xl font-bold text-[#2c2c2c] mb-4" data-id="element-235">
              Core Values
            </h2>
            <p className="text-[#525252]" data-id="element-236">
              The principles that guide every project we undertake.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-237">
            {[{
            icon: Users,
            title: 'Relationships',
            desc: 'Building lasting partnerships through trust and transparency.'
          }, {
            icon: Award,
            title: 'Quality',
            desc: 'Never compromising on materials or craftsmanship.'
          }, {
            icon: Clock,
            title: 'Reliability',
            desc: 'Showing up on time and finishing what we start.'
          }].map((value, idx) => <motion.div key={idx} whileHover={{
            y: -5
          }} className="p-8 bg-gray-50 rounded-2xl border border-gray-100 text-center" data-id="element-238">
                <div className="w-14 h-14 mx-auto bg-white rounded-xl shadow-sm flex items-center justify-center text-[#17a2b8] mb-6" data-id="element-239">
                  <value.icon className="h-7 w-7" data-id="element-240" />
                </div>
                <h3 className="text-xl font-bold text-[#2c2c2c] mb-3" data-id="element-241">
                  {value.title}
                </h3>
                <p className="text-[#525252]" data-id="element-242">{value.desc}</p>
              </motion.div>)}
          </div>
        </Section>
      </main>

      <Footer data-id="element-243" />
    </div>;
}